import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FiChevronDown, FiCheck } from "react-icons/fi";

/**
 * CustomDropdown - A fully styled dropdown component
 *
 * @param {Array} options - Array of { value, label, icon? } objects
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when value changes
 * @param {string} className - Additional classes for the trigger button
 * @param {string} menuClassName - Additional classes for the dropdown menu
 * @param {string} placeholder - Placeholder text when no value selected
 * @param {boolean} showCheckmark - Show checkmark on selected option
 * @param {number} maxWidth - Maximum width for the dropdown (default: 280)
 */
const CustomDropdown = ({
  options,
  value,
  onChange,
  className = "",
  menuClassName = "",
  placeholder = "Select...",
  showCheckmark = true,
  renderTrigger,
  renderOption,
  maxWidth = 280,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [minWidth, setMinWidth] = useState(0);
  const dropdownRef = useRef(null);
  const measureRef = useRef(null);

  // Measure the longest option to set consistent width
  useEffect(() => {
    if (measureRef.current) {
      const width = measureRef.current.offsetWidth;
      setMinWidth(Math.min(width, maxWidth));
    }
  }, [options, maxWidth]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  // Find the longest label for measurement
  const longestLabel =
    options.reduce(
      (longest, opt) =>
        opt.label.length > longest.length ? opt.label : longest,
      placeholder
    ) || placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Hidden element to measure the longest option */}
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute whitespace-nowrap px-3 py-1.5 text-sm font-medium"
        aria-hidden="true"
      >
        {longestLabel}
        {/* Add space for chevron icon + checkmark if shown */}
        <span className={showCheckmark ? "ml-12" : "ml-6"} />
      </div>

      {/* Trigger Button */}
      {renderTrigger ? (
        <div
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
          role="button"
          tabIndex={0}
          className="cursor-pointer"
          style={{ minWidth: minWidth > 0 ? minWidth : undefined }}
        >
          {renderTrigger(selectedOption, isOpen, minWidth)}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{ minWidth: minWidth > 0 ? minWidth : undefined }}
          className={`flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${className}`}
        >
          <span>{selectedOption?.label || placeholder}</span>
          <FiChevronDown
            className={`size-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{ minWidth: minWidth > 0 ? minWidth : undefined }}
          className={`absolute z-50 mt-1 min-w-full overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg ${menuClassName}`}
        >
          {options.map((option) => {
            const isSelected = option.value === value;

            if (renderOption) {
              return (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect(option.value);
                    }
                  }}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  className="cursor-pointer"
                >
                  {renderOption(option, isSelected)}
                </div>
              );
            }

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                  isSelected
                    ? "bg-gray-50 font-medium text-gray-900"
                    : "text-gray-700"
                }`}
              >
                {option.icon && <span className="size-4">{option.icon}</span>}
                <span className="flex-1">{option.label}</span>
                {showCheckmark && isSelected && (
                  <FiCheck className="size-4 text-primary-600" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

CustomDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  menuClassName: PropTypes.string,
  placeholder: PropTypes.string,
  showCheckmark: PropTypes.bool,
  renderTrigger: PropTypes.func,
  renderOption: PropTypes.func,
  maxWidth: PropTypes.number,
};

export default CustomDropdown;
